/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './FddPipeline.module.scss';

type Translate = (key: string) => string;

const getSteps = (t: Translate) => [
  {
    num: '01',
    title: t('We collect the filing'),
    desc: <>{t('Direct connections to the registers themselves. No intermediary holds the document before we do.')}</>,
    highlight: false,
  },
  {
    num: '02',
    title: t('We read the document'),
    desc: (
      <>
        <strong className={styles.strong}>{t('OCR and AI')}</strong>{' '}
        {t(
          'across every page. A scanned or paper filing becomes line items, rather than an attachment someone opens by hand.',
        )}
      </>
    ),
    highlight: true,
  },
  {
    num: '03',
    title: t('We extract and map'),
    desc: (
      <>{t('Line items pulled out and resolved to one financial model, across templates, languages and scripts.')}</>
    ),
    highlight: false,
  },
  {
    num: '04',
    title: t('We attach the evidence'),
    desc: <>{t('Register, filing reference and last-updated date travel with every figure we publish.')}</>,
    highlight: false,
  },
];

const getStats = (t: Translate) => [
  { value: '400+', label: t('government registers connected directly') },
  { value: '200+', label: t('countries and territories in scope') },
  { value: '10 years', label: t('of filing history retained') },
  { value: t('Daily'), label: t('re-read of every monitored company') },
];

export const FddPipeline = () => {
  const { t } = useInt();
  const steps = getSteps(t);
  const stats = getStats(t);
  const headRef = useRef<HTMLDivElement>(null);
  const stageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const statRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const revealables = [headRef.current, ...stageRefs.current, ...statRefs.current].filter(
      (el): el is HTMLDivElement => Boolean(el),
    );

    if (!revealables.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealVisible);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 },
    );

    revealables.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <Section className={styles.root}>
      <div ref={headRef} className={cn(styles.head, styles.reveal)}>
        <TitleBlock
          title={t('We built the whole path, from the register to the figure.')}
          description={t(
            'The filing is collected by us, read by us and published by us. That unbroken path is the reason a figure still carries its filing when someone reviews your file months later.',
          )}
          align="left"
        />
      </div>

      <div className={styles.rail}>
        {steps.map((step, i) => (
          <div
            key={step.num}
            ref={(el) => {
              stageRefs.current[i] = el;
            }}
            className={cn(styles.rstage, step.highlight && styles.rstageHl, styles.reveal)}
            style={{ transitionDelay: `${i * 90}ms` }}
          >
            <span className={styles.stepNum}>{step.num}</span>
            <h3 className={styles.stepTitle}>{step.title}</h3>
            <p className={styles.stepDesc}>{step.desc}</p>
          </div>
        ))}
      </div>

      <div className={styles.statsBar}>
        {stats.map((s, i) => (
          <div
            key={i}
            ref={(el) => {
              statRefs.current[i] = el;
            }}
            className={cn(styles.statItem, styles.reveal)}
            style={{ transitionDelay: `${i * 90}ms` }}
          >
            <span className={styles.statValue}>{s.value}</span>
            <span className={styles.statLabel}>{s.label}</span>
          </div>
        ))}
      </div>
    </Section>
  );
};
